// frontend/src/components/pages/admin/usage/OrganizationsUsageTab.jsx
import React, { useEffect, useState } from "react";
import {
  getUsageByRole,
  getUsageByOrganizationId,
  downloadInvoicePdf,
  sendInvoiceEmail as sendInvoiceEmailApi,
} from "../../../../api/apiFunction/usageServices";
import { getAllOrganizations } from "../../../../api/apiFunction/orgServices";
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronRight as ChevronRightIcon,
  Send,
  ArrowDownToLine,
} from "lucide-react";
import UsageStatsCards from "./UsageStatsCards";
import "../../../../styles/UsageCards.css";
import { toast } from "react-toastify";

const UsageBentoCard = ({ loading, error, usage }) => {
  const [statsTab, setStatsTab] = useState("daily");

  const formatDate = (dateString) => {
    if (!dateString) return "No activity";
    const date = new Date(dateString);
    const options = {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    };
    return date.toLocaleString("en-US", options);
  };

  return (
    <div className="bg-bg-60 p-6">
      {loading ? (
        <div className="flex items-center justify-center h-24">
          Loading usage...
        </div>
      ) : error ? (
        <div className="flex items-center justify-center h-24 text-red-500">
          {error}
        </div>
      ) : usage ? (
        <>
          <div className="text-xs text-fg-60 uppercase mb-2">USAGE SUMMARY</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 w-full mb-6">
            <div className="bg-bg-30 rounded-lg p-4 w-full flex flex-col items-center justify-center">
              <div className="text-xs text-fg-60 mb-1">Total Tokens</div>
              <div className="text-lg font-semibold text-fg-50">
                {(
                  (usage.totals?.highEndTokens || 0) +
                  (usage.totals?.smallTokens || 0)
                ).toLocaleString()}
              </div>
            </div>
            <div className="bg-bg-30 rounded-lg p-4 w-full flex flex-col items-center justify-center">
              <div className="text-xs text-fg-60 mb-1">Total Cost</div>
              <div className="text-lg font-semibold text-fg-50">
                ${usage.totalCost?.toFixed(6) || "0.00"}
              </div>
            </div>
            <div className="bg-bg-30 rounded-lg p-4 w-full flex flex-col items-center justify-center">
              <div className="text-xs text-fg-60 mb-1">Total Requests</div>
              <div className="text-lg font-semibold text-fg-50">
                {usage.totalRequests?.toLocaleString() || 0}
              </div>
            </div>
            <div className="bg-bg-30 rounded-lg p-4 w-full flex flex-col items-center justify-center">
              <div className="text-xs text-fg-60 mb-1">Last Activity</div>
              <div className="text-sm font-semibold text-fg-50 text-center">
                {usage.lastActivity
                  ? formatDate(usage.lastActivity.timestamp)
                  : "No activity"}
              </div>
            </div>
          </div>
          {/* Stats Tabs */}
          <div className="text-xs text-fg-60 uppercase mb-4">USAGE HISTORY</div>
          <div className="flex gap-2 mb-4">
            <button
              className={`py-1.5 px-4 text-sm font-medium rounded-lg ${
                statsTab === "daily"
                  ? "bg-bg-40 text-fg-90 shadow-sm"
                  : "bg-transparent text-fg-60 hover:text-fg-80"
              }`}
              onClick={() => setStatsTab("daily")}
            >
              Daily
            </button>
            <button
              className={`py-1.5 px-4 text-sm font-medium rounded-lg ${
                statsTab === "weekly"
                  ? "bg-bg-40 text-fg-90 shadow-sm"
                  : "bg-transparent text-fg-60 hover:text-fg-80"
              }`}
              onClick={() => setStatsTab("weekly")}
            >
              Weekly
            </button>
            <button
              className={`py-1.5 px-4 text-sm font-medium rounded-lg ${
                statsTab === "monthly"
                  ? "bg-bg-40 text-fg-90 shadow-sm"
                  : "bg-transparent text-fg-60 hover:text-fg-80"
              }`}
              onClick={() => setStatsTab("monthly")}
            >
              Monthly
            </button>
          </div>
          {/* Stats Table */}
          <div className="bg-bg-60 rounded-lg shadow-sm overflow-hidden">
            <table className="min-w-full divide-y divide-bd-50">
              <thead className="bg-bg-40">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                    {statsTab === "daily"
                      ? "Date"
                      : statsTab === "weekly"
                      ? "Week Range"
                      : "Month"}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                    Requests
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                    Tokens
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                    Cost
                  </th>
                </tr>
              </thead>
              <tbody className="bg-bg-60 divide-y divide-bd-50">
                {(statsTab === "daily"
                  ? usage.daily
                  : statsTab === "weekly"
                  ? usage.weekly
                  : usage.monthly) &&
                (statsTab === "daily"
                  ? usage.daily
                  : statsTab === "weekly"
                  ? usage.weekly
                  : usage.monthly
                ).length > 0 ? (
                  (statsTab === "daily"
                    ? usage.daily
                    : statsTab === "weekly"
                    ? usage.weekly
                    : usage.monthly
                  ).map((stat, idx) => (
                    <tr key={idx}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-50">
                        {statsTab === "daily"
                          ? stat.date
                          : statsTab === "weekly"
                          ? stat.weekRange
                          : stat.month}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-50">
                        {stat.requests?.toLocaleString() || "0"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-50">
                        {(
                          (stat.highEndTokens || 0) + (stat.smallTokens || 0)
                        ).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-50">
                        ${stat.cost?.toFixed(6) || "0.000000"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="3"
                      className="px-6 py-4 text-center text-sm text-fg-60"
                    >
                      No {statsTab} stats found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
};

const OrganizationsUsageTab = () => {
  const [tab, setTab] = useState("overall");
  const [statsTab, setStatsTab] = useState("daily"); // 'daily' | 'weekly' | 'monthly'
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [orgsError, setOrgsError] = useState(null);
  const [expandedOrgId, setExpandedOrgId] = useState(null);
  const [perOrgUsage, setPerOrgUsage] = useState({});
  const [perOrgLoading, setPerOrgLoading] = useState({});
  const [perOrgError, setPerOrgError] = useState({});
  const [perOrgEmailLoading, setPerOrgEmailLoading] = useState({});
  const PAGE_SIZE = 10;
  const [orgsPage, setOrgsPage] = useState(1);
  const [orgsTotalPages, setOrgsTotalPages] = useState(1);

  useEffect(() => {
    if (tab === "overall") {
      setLoading(true);
      setError(null);
      setUsage(null);
      getUsageByRole("organization")
        .then(setUsage)
        .catch((err) => setError(err?.error || "Failed to fetch usage"))
        .finally(() => setLoading(false));
    }
  }, [tab]);

  useEffect(() => {
    if (tab === "per") {
      setOrgsLoading(true);
      setOrgsError(null);
      setOrgs([]);
      setExpandedOrgId(null);
      getAllOrganizations({ page: orgsPage, limit: PAGE_SIZE })
        .then((res) => {
          setOrgs(res?.data?.organizations || res?.data || []);
          setOrgsTotalPages(res?.data?.pagination?.totalPages || 1);
        })
        .catch(() => setOrgsError("Failed to fetch organizations"))
        .finally(() => setOrgsLoading(false));
    }
  }, [tab, orgsPage]);

  useEffect(() => {
    if (tab === "per" && expandedOrgId) {
      setPerOrgLoading((prev) => ({ ...prev, [expandedOrgId]: true }));
      setPerOrgError((prev) => ({ ...prev, [expandedOrgId]: null }));
      setPerOrgUsage((prev) => ({ ...prev, [expandedOrgId]: null }));
      getUsageByOrganizationId(expandedOrgId)
        .then((data) =>
          setPerOrgUsage((prev) => ({ ...prev, [expandedOrgId]: data }))
        )
        .catch(() =>
          setPerOrgError((prev) => ({
            ...prev,
            [expandedOrgId]: "Failed to fetch organization usage",
          }))
        )
        .finally(() =>
          setPerOrgLoading((prev) => ({ ...prev, [expandedOrgId]: false }))
        );
    }
  }, [tab, expandedOrgId]);

  const stats = usage
    ? [
        {
          label: "Total Tokens",
          value: (
            (usage.totals?.highEndTokens || 0) +
            (usage.totals?.smallTokens || 0)
          ).toLocaleString(),
          colorVar: "--usage-bg-01",
          fgVar: "--usage-fg-01",
          unit: "tokens",
        },
        {
          label: "Total Cost",
          value: `$${usage.totals?.totalCost?.toFixed(6) || "0.00"}`,
          colorVar: "--usage-bg-02",
          fgVar: "--usage-fg-02",
          unit: "USD",
        },
        {
          label: "Total Requests",
          value: usage.totals?.totalRequests?.toLocaleString() || "0",
          colorVar: "--usage-bg-03",
          fgVar: "--usage-fg-03",
          unit: "requests",
        },
      ]
    : [];

  return (
    <div className="w-full flex justify-center p-6 h-full overflow-y-auto custom-scrollbar">
      <div className="max-w-7xl w-full">
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            className={`py-1.5 px-4 text-sm font-medium transition-colors duration-150 border-none outline-none focus:outline-none rounded-lg ${
              tab === "overall"
                ? "bg-bg-40 text-fg-90 border-b-2 border-ac-02 shadow-sm"
                : "bg-transparent text-fg-60 hover:text-fg-80"
            }`}
            style={{
              boxShadow:
                tab === "overall" ? "0 1px 4px 0 rgba(0,0,0,0.03)" : "none",
            }}
            onClick={() => setTab("overall")}
          >
            Overall
          </button>
          <button
            className={`py-1.5 px-4 text-sm font-medium transition-colors duration-150 border-none outline-none focus:outline-none rounded-lg ${
              tab === "per"
                ? "bg-bg-40 text-fg-90 border-b-2 border-ac-02 shadow-sm"
                : "bg-transparent text-fg-60 hover:text-fg-80"
            }`}
            style={{
              boxShadow:
                tab === "per" ? "0 1px 4px 0 rgba(0,0,0,0.03)" : "none",
            }}
            onClick={() => setTab("per")}
          >
            Per Organization
          </button>
        </div>
        {/* Overall Tab */}
        {tab === "overall" &&
          (loading ? (
            <div className="text-fg-60 flex items-center justify-center h-[60vh] w-full">
              <span>Loading...</span>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-[60vh] w-full text-red-500">
              {error}
            </div>
          ) : usage ? (
            <>
              <div className="w-full mb-6">
                <UsageStatsCards
                  stats={stats}
                  title="Organization Usage Stats"
                />
              </div>
              {/* Stats Tabs */}
              <div className="text-base font-semibold mb-4">Usage History</div>
              <div className="flex gap-2 mb-4">
                <button
                  className={`py-1.5 px-4 text-sm font-medium rounded-lg ${
                    statsTab === "daily"
                      ? "bg-bg-40 text-fg-90 shadow-sm"
                      : "bg-transparent text-fg-60 hover:text-fg-80"
                  }`}
                  onClick={() => setStatsTab("daily")}
                >
                  Daily
                </button>
                <button
                  className={`py-1.5 px-4 text-sm font-medium rounded-lg ${
                    statsTab === "weekly"
                      ? "bg-bg-40 text-fg-90 shadow-sm"
                      : "bg-transparent text-fg-60 hover:text-fg-80"
                  }`}
                  onClick={() => setStatsTab("weekly")}
                >
                  Weekly
                </button>
                <button
                  className={`py-1.5 px-4 text-sm font-medium rounded-lg ${
                    statsTab === "monthly"
                      ? "bg-bg-40 text-fg-90 shadow-sm"
                      : "bg-transparent text-fg-60 hover:text-fg-80"
                  }`}
                  onClick={() => setStatsTab("monthly")}
                >
                  Monthly
                </button>
              </div>
              {/* Stats Table */}
              <div className="bg-bg-60 rounded-lg shadow-sm overflow-hidden">
                <table className="min-w-full divide-y divide-bd-50">
                  <thead className="bg-bg-40">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                        {statsTab === "daily"
                          ? "Date"
                          : statsTab === "weekly"
                          ? "Week Range"
                          : "Month"}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                        Requests
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                        Tokens
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                        Cost
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-bg-60 divide-y divide-bd-50">
                    {(statsTab === "daily"
                      ? usage.daily
                      : statsTab === "weekly"
                      ? usage.weekly
                      : usage.monthly) &&
                    (statsTab === "daily"
                      ? usage.daily
                      : statsTab === "weekly"
                      ? usage.weekly
                      : usage.monthly
                    ).length > 0 ? (
                      (statsTab === "daily"
                        ? usage.daily
                        : statsTab === "weekly"
                        ? usage.weekly
                        : usage.monthly
                      ).map((stat, idx) => (
                        <tr key={idx}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-50">
                            {statsTab === "daily"
                              ? stat.date
                              : statsTab === "weekly"
                              ? stat.weekRange
                              : stat.month}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-50">
                            {stat.requests?.toLocaleString() || "0"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-50">
                            {(
                              (stat.highEndTokens || 0) +
                              (stat.smallTokens || 0)
                            ).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-50">
                            ${stat.cost?.toFixed(6) || "0.000000"}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="4"
                          className="px-6 py-4 text-center text-sm text-fg-60"
                        >
                          No {statsTab} stats found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : null)}
        {/* Per Organization Tab */}
        {tab === "per" && (
          <div>
            {orgsLoading ? (
              <div className="flex items-center justify-center h-[40vh] w-full">
                <span>Loading organizations...</span>
              </div>
            ) : orgsError ? (
              <div className="flex items-center justify-center h-[40vh] w-full text-red-500">
                {orgsError}
              </div>
            ) : (
              <>
                <div className="mb-4">
                  <h2 className="text-base font-semibold mb-2">
                    All Organizations
                  </h2>
                  <div className="overflow-x-auto bg-bg-60 rounded-lg">
                    <table className="min-w-full divide-y divide-bd-50 border border-gray-200 rounded-lg overflow-hidden">
                      <thead className="bg-bg-40">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                            Domain
                          </th>
                          <th className="px-4 py-3 w-48 text-center text-xs font-medium text-fg-60 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-bg-60 divide-y divide-bd-50">
                        {orgs.length > 0 ? (
                          orgs.flatMap((org) => {
                            const orgId = org._id || org.id;
                            const isExpanded = expandedOrgId === orgId;

                            return [
                              <tr
                                key={orgId}
                                className={isExpanded ? "!border-t-0" : ""}
                              >
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-50">
                                  {org.name}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-50">
                                  {org.domain}
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-fg-50">
                                  <div className="flex justify-end gap-2 items-center action-buttons-row w-full">
                                    <button
                                      className={`flex items-center gap-2 px-3 py-1.5 rounded bg-ac-02 text-white dark:text-black hover:bg-ac-01 transition-colors text-xs font-medium ${
                                        isExpanded ? "ring-2 ring-ac-02" : ""
                                      }`}
                                      onClick={() =>
                                        setExpandedOrgId(
                                          isExpanded ? null : orgId
                                        )
                                      }
                                      aria-expanded={isExpanded}
                                      aria-controls={`usage-row-${orgId}`}
                                    >
                                      {isExpanded ? "Hide Usage" : "View Usage"}
                                      {isExpanded ? (
                                        <ChevronDown
                                          size={16}
                                          className="w-4 h-4"
                                        />
                                      ) : (
                                        <ChevronRightIcon
                                          size={16}
                                          className="w-4 h-4"
                                        />
                                      )}
                                    </button>
                                    <div className="flex gap-2 ml-3">
                                      <button
                                        className="p-2 rounded text-fg-80 hover:bg-bg-40 transition-colors flex items-center justify-center"
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          try {
                                            const response =
                                              await downloadInvoicePdf(orgId);
                                            const blob = new Blob(
                                              [response.data],
                                              { type: "application/pdf" }
                                            );
                                            const url =
                                              window.URL.createObjectURL(blob);
                                            const link =
                                              document.createElement("a");
                                            link.href = url;
                                            link.setAttribute(
                                              "download",
                                              `invoice-${orgId}.pdf`
                                            );
                                            document.body.appendChild(link);
                                            link.click();
                                            document.body.removeChild(link);
                                            window.URL.revokeObjectURL(url);
                                          } catch (error) {
                                            alert(
                                              "Failed to download invoice PDF."
                                            );
                                          }
                                        }}
                                        title="Download Invoice PDF"
                                      >
                                        <ArrowDownToLine size={17} />
                                      </button>
                                      <button
                                        className="p-2 rounded text-fg-80 hover:bg-bg-40 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                        disabled={perOrgEmailLoading[orgId]}
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          setPerOrgEmailLoading((prev) => ({
                                            ...prev,
                                            [orgId]: true,
                                          }));
                                          try {
                                            await sendInvoiceEmailApi(orgId);
                                            toast.success(
                                              "Invoice email sent successfully!"
                                            );
                                          } catch {
                                            alert(
                                              "Failed to send invoice email."
                                            );
                                          } finally {
                                            setPerOrgEmailLoading((prev) => ({
                                              ...prev,
                                              [orgId]: false,
                                            }));
                                          }
                                        }}
                                        title="Send Invoice Email"
                                      >
                                        <Send size={16} />
                                      </button>
                                    </div>
                                  </div>
                                </td>
                              </tr>,
                              isExpanded && (
                                <tr key={`usage-row-${orgId}`}>
                                  <td
                                    colSpan="3"
                                    className="p-0 border-none bg-transparent"
                                  >
                                    <UsageBentoCard
                                      loading={perOrgLoading[orgId]}
                                      error={perOrgError[orgId]}
                                      usage={perOrgUsage[orgId]}
                                    />
                                  </td>
                                </tr>
                              ),
                            ];
                          })
                        ) : (
                          <tr>
                            <td
                              colSpan="3"
                              className="text-center text-fg-60 py-8"
                            >
                              No organizations found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {/* Pagination */}
                  <div className="flex justify-center items-center gap-2 mt-4 pb-6">
                    <button
                      className="p-1 rounded disabled:opacity-50"
                      onClick={() => setOrgsPage((p) => Math.max(1, p - 1))}
                      disabled={orgsPage === 1}
                      aria-label="Previous page"
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <span className="px-2 text-base font-semibold">
                      {orgsPage}
                    </span>
                    <button
                      className="p-1 rounded disabled:opacity-50"
                      onClick={() =>
                        setOrgsPage((p) => Math.min(orgsTotalPages, p + 1))
                      }
                      disabled={orgsPage === orgsTotalPages}
                      aria-label="Next page"
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrganizationsUsageTab;
